import * as React from "react"
import {OnFailure, OnSuccess, OnSuccessData} from "../../../../store/action/actionHelpers";
import Toast from "../../../helper/Toast";
import {connect, Dispatch} from "react-redux";
import {editIndividualContact, updateArchivedStatus} from "../../../../store/action/contacts/contacts";
import IndividualContact from "../../../../model/contact/IndividualContact";
import IndividualContactData from "../../../../model/contact/individual/IndividualContactData";
import EditIndividualContactFormView from "./EditIndividualContactFormView";
import {none} from "ts-option";

interface ComponentProps {
	/**
	 * On success callback.
	 */
	onSuccess?: OnSuccessData<IndividualContact>;
	/**
	 * Contact to edit.
	 */
	contact: IndividualContact;
	/**
	 * On cancel callback
	 */
	onCancel?: () => void;
}


interface DispatchProps {
	/**
	 * Edit contact action.
	 * @param {IndividualContact} contact Contact to update.
	 * @param {OnSuccess} onSuccess on contact edit success callback.
	 * @param {OnFailure} onFailure on contact edit failure callback.
	 */
	editContact: (contact: IndividualContact, onSuccess: OnSuccessData<IndividualContact>, onFailure: OnFailure) => void,
	/**
	 * Update contact "archived" flag value.
	 * @param {string} contactId
	 * @param {boolean} archived
	 * @param {OnSuccessData<IndividualContact>} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateArchivedStatus: (contactId: string,
		archived: boolean,
		onSuccess: OnSuccess,
		onFailure: OnFailure) => void;
}

interface State {
	/**
	 * Indicates if data is loading.
	 */
	isLoading: boolean;
}

type Props = ComponentProps & DispatchProps;

/**
 * Edit contact form.
 */
class EditContactForm extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {isLoading: false};
		this.onSuccess = this.onSuccess.bind(this);
		this.onFailure = this.onFailure.bind(this);
		this.editContact = this.editContact.bind(this);
	}

	render() {
		return (
			<EditIndividualContactFormView label={"Edit Contact"}
										   isLoading={this.state.isLoading}
										   model={this.props.contact}
										   onSubmit={this.editContact}
										   onCancel={this.props.onCancel}/>
		);
	}

	/**
	 * On successfully edited action.
	 * @param {IndividualContact} contact Currently edited contact.
	 */
	private onSuccess(contact: IndividualContact): void {
		this.setState({isLoading: false});
		if (this.props.onSuccess) {
			this.props.onSuccess(contact);
		}
	}

	/**
	 * On editing contact failure.
	 */
	private onFailure() {
		this.setState({isLoading: false});
		Toast.error("Sorry, something went wrong. Try again later.", "Edit contact error");
	}

	/**
	 * Edit contact.
	 * @param {IndividualContactData} contactData Contact data.
	 * @param {boolean} updateArchivedStatus if true, contact "archived" flag value is updated.
	 */
	private editContact(contactData: IndividualContactData, updateArchivedStatus: boolean) {
		this.setState({isLoading: true}, () => this.props.editContact(
			new IndividualContact(this.props.contact.id, contactData), (editedContact) => {
				this.onEditSuccess(editedContact.copy({data: editedContact.data.copy({archived: contactData.archived})}),
					updateArchivedStatus)
			}, this.onFailure)
		);
	}

	private onEditSuccess = (contact: IndividualContact, updateArchivedStatus: boolean) => {
		if (updateArchivedStatus) {
			this.props.updateArchivedStatus(this.props.contact.id, contact.data.archived, () => this.onSuccess(contact), this.onFailure);
		}
		else {
			this.onSuccess(contact);
		}
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		editContact: (contact: IndividualContact, onSuccess: OnSuccessData<IndividualContact>, onFailure: OnFailure) => dispatch(
			editIndividualContact(contact, none, false, onSuccess, onFailure)
		),
		updateArchivedStatus: (contactId: string,
			archived: boolean,
			onSuccess: OnSuccess,
			onFailure: OnFailure) =>
			dispatch(updateArchivedStatus(contactId, archived, onSuccess, onFailure))
	}
}

export default connect(null, mapDispatchToProps)(EditContactForm)